import { ApiKey } from "./api-keys";
import { Member } from "./member";
import { Organization } from "./organization";
import { TaxExemption } from "./tax-exemption";
import { User } from "./user";

export interface Subscription {
  id: string;
  seats: number;
  cancelAt: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;
  productId: string;
  priceId: string;
  status: string;
  paymentMethod: SubscriptionCollectionMethod;
}

export type SubscriptionProgress = {
  percent: number;
  isOverLimit: boolean;
};

export interface SubscriptionProps {
  isAdmin: boolean;
  isMatchLoading: boolean;
  user: User;
  organization: Organization | null;
  subscription: Subscription | null;
  apiKeys: ApiKey[];
  paymentMethod: PaymentMethod;
  members: Member[];
  taxExemption: TaxExemption | null;
  onSubscribe: () => void;
  onMatch: () => void;
  onQuantityChange: (product: Product) => void;
}

export interface SubscriptionCustomer {
  companyName: string;
  email: string;
  streetAddress: string;
  streetAddress2: string;
  city: string;
  state: string;
  postalCode: string;
}

export enum SubscriptionCollectionMethod {
  AUTOMATIC = "charge_automatically",
  INVOICE = "send_invoice",
}

export const PaymentMethods = {
  ONE_TIME: {
    type: "one_time",
    name: "One-Time",
    term: "one-time",
    powerOnPipelinesPrice: 0,
    powerOnPipelinesPriceId: null,
    powerOnPipelinesPaymentUrl: null,
    powerOnProPrice: 0,
    powerOnProPriceId: null,
    powerOnProPaymentUrl: null,
  },
  MONTHLY: {
    type: "monthly",
    name: "Monthly",
    term: "month",
    powerOnPipelinesPrice: 0,
    powerOnPipelinesPriceId: null,
    powerOnPipelinesPaymentUrl: null,
    powerOnProPrice: 60,
    powerOnProPriceId: process.env.NEXT_PUBLIC_STRIPE_POWERON_MONTHLY_PRICE_ID,
    powerOnProPaymentUrl:
      process.env.NEXT_PUBLIC_STRIPE_POWERON_MONTHLY_PAYMENT_URL,
  },
  YEARLY: {
    type: "yearly",
    name: "Yearly",
    term: "year",
    powerOnPipelinesPrice: 3089,
    powerOnPipelinesPriceId:
      process.env.NEXT_PUBLIC_STRIPE_PIPELINES_YEARLY_PRICE_ID,
    powerOnPipelinesPaymentUrl:
      process.env.NEXT_PUBLIC_STRIPE_PIPELINES_YEARLY_PAYMENT_URL,
    powerOnProPrice: 684,
    powerOnProPriceId: process.env.NEXT_PUBLIC_STRIPE_POWERON_YEARLY_PRICE_ID,
    powerOnProPaymentUrl:
      process.env.NEXT_PUBLIC_STRIPE_POWERON_YEARLY_PAYMENT_URL,
  },
  THREE_YEAR: {
    type: "three_year",
    name: "3-Year",
    term: "3-years",
    powerOnPipelinesPrice: 8343,
    powerOnPipelinesPriceId:
      process.env.NEXT_PUBLIC_STRIPE_PIPELINES_THREE_YEAR_PRICE_ID,
    powerOnPipelinesPaymentUrl:
      process.env.NEXT_PUBLIC_STRIPE_PIPELINES_THREE_YEAR_PAYMENT_URL,
    powerOnProPrice: 1944,
    powerOnProPriceId:
      process.env.NEXT_PUBLIC_STRIPE_POWERON_THREE_YEAR_PRICE_ID,
    powerOnProPaymentUrl:
      process.env.NEXT_PUBLIC_STRIPE_POWERON_THREE_YEAR_PAYMENT_URL,
  },
} as const;

export type PaymentMethod =
  (typeof PaymentMethods)[keyof typeof PaymentMethods]["type"];

export type Product = "poweron-pro" | "poweron-pipelines";

export interface ValidatedPromotionCode {
  id: string;
  code: string;
  active: boolean;
  coupon: {
    id: string;
    name: string | null;
    percentOff: number | null;
    amountOff: number | null;
    currency: string | null;
    duration: string;
    durationInMonths: number | null;
    valid: boolean;
  };
}

/* Type helpers */

/**
 * Gets the subscription metadata based on the subscription type
 * @param type The subscription type
 */
export const getSubscriptionMetadata = (type: PaymentMethod) => {
  return Object.values(PaymentMethods).find((s) => s.type === type)!;
};

/**
 * Gets the subscription type based on the price ID
 * @param priceId The price ID in Stripe
 */
export const getPaymentMethodByPriceId = (
  priceId: string,
): PaymentMethod | undefined => {
  const sub = Object.values(PaymentMethods).find(
    (s) =>
      s.powerOnProPriceId === priceId || s.powerOnPipelinesPriceId === priceId,
  );
  return sub?.type;
};

/**
 * Gets the subscription price based on the price ID
 * @param priceId The price ID in Stripe
 */
export const getSubscriptionPriceByPriceId = (
  priceId: string,
): number | undefined => {
  const sub = Object.values(PaymentMethods).find(
    (s) =>
      s.powerOnProPriceId === priceId || s.powerOnPipelinesPriceId === priceId,
  );

  if (!sub) return undefined;

  if (sub.powerOnProPriceId === priceId) return sub.powerOnProPrice;
  if (sub.powerOnPipelinesPriceId === priceId) return sub.powerOnPipelinesPrice;
};

/**
 * Gets the subscription product ID based on the product type
 * @param product The product type
 */
export const getSubscriptionProductIdByProduct = (product: Product): string => {
  switch (product) {
    case "poweron-pro":
      return process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID!;
    case "poweron-pipelines":
      return process.env.NEXT_PUBLIC_STRIPE_PIPELINES_PRODUCT_ID!;
    default:
      throw new Error(`Unknown product: ${product}`);
  }
};

/**
 * Gets the product name based on the product type
 * @param product The product type
 */
export const getProductNameByProduct = (product: Product): string => {
  switch (product) {
    case "poweron-pro":
      return "PowerOn Pro";
    case "poweron-pipelines":
      return "PowerOn Pipelines";
    default:
      throw new Error(`Unknown product: ${product}`);
  }
};
